import jsPDF from 'jspdf';
import {TasksService} from '../../../../../services/openapi/services/TasksService';
import {ArbeitszeitService} from '../../../../../services/openapi/services/ArbeitszeitService';
import {Task} from '../../../../../services/openapi/models/Task';
import {TaskImageBase64} from '../../../../../services/openapi/models/TaskImageBase64';
import {BauvorhabenWithID} from '../../../../../services/openapi/models/BauvorhabenWithID';

// Import utility functions
import {addFooter, addKeyValue, addSection, addSignatureSection, checkAndAddPageBreak, ensureSpace, formatDate, formatDateTime, generateFileName, loadCustomFont, tryLoadLogoImage} from './pdfUtils';

import {addImageToPDF, convertFilesToBase64, ImageData} from './imageUtils';

import {loadTaskImagesBase64, showImageSelectionDialog} from './rapportHelpers';

/**
 * Parameters for creating a rapport PDF
 */
interface CreateRapportParams {
    taskId: number;
    task: Task;
    users?: any[];
    bau?: BauvorhabenWithID[];
    projekt_name?: string;
    formatDate?: (date: string) => string;
    getCurrentTimeFrom?: () => any;
    getCurrentTimeTo?: () => any;
    getTotalDuration?: () => any;
    getStatusLabel?: (status?: any) => string;
    getRapportText?: () => string;
    showSnackbar?: any;
    setOpenEndTask?: any;
    selectedImages?: number[];
    signature?: string | null;
    uploadedFiles?: File[];
    anfahrt_abfahrt_mit_kfz?: boolean;
    material?: string;
    arbeitszeit?: number;
    anzahl_monteure?: number;
}

/**
 * Load Bauvorhaben name for task
 */
const loadBauvorhabenName = async (bauvorhabenId?: number): Promise<string> => {
    if (!bauvorhabenId) return '';

    try {
        const allBauvorhaben = await ArbeitszeitService.getAlleBauvorhabenArbeitzeitBauvorhabenGet(false);
        const bauvorhaben = allBauvorhaben.find((bvh: BauvorhabenWithID) => bvh.id === bauvorhabenId);
        return bauvorhaben ? bauvorhaben.bvh_name : `Bauvorhaben ${bauvorhabenId}`;
    } catch (error) {
        console.warn('Could not load Bauvorhaben details:', error);
        return `Bauvorhaben ${bauvorhabenId}`;
    }
};

/**
 * Get Bauvorhaben name from local bau array (sync version)
 */
const getBauvorhabenName = (
    bauvorhabenId: number | undefined,
    projektName: string | undefined,
    bau: BauvorhabenWithID[]
): string => {
    if (!bauvorhabenId) {
        return projektName || 'Kein Bauvorhaben';
    }
    const bvh = bau.find(b => b.id === bauvorhabenId);
    return bvh?.bvh_name || 'Unbekanntes Bauvorhaben';
};

/**
 * Add task information section to PDF
 */
const addTaskInfoSection = (
    doc: jsPDF,
    task: Task,
    currentY: number,
    statusLabel: string,
    closedTimestamp: string,
    bau: BauvorhabenWithID[] = [],
    projektName?: string
): number => {
    let y = currentY;

    // Task Information
    y = addSection(doc, 'Aufgaben Informationen', y);
    const liegenschaftName = getBauvorhabenName(task.bauvorhaben_id, projektName || (task as any).projekt_name, bau);
    if (liegenschaftName && liegenschaftName !== 'Kein Bauvorhaben') {
        y = addKeyValue(doc, 'Liegenschaft', liegenschaftName, 20, y);
    }
    y = addKeyValue(doc, 'Arbeitsauftrag', task.title || '', 20, y);
    if (task.description) {
        y = addKeyValue(doc, 'Beschreibung', task.description, 20, y);
    }

    y = addKeyValue(doc, 'Status', statusLabel, 20, y);
    y = addKeyValue(doc, 'Datum Ausführung', formatDateTime(closedTimestamp), 20, y);

    // Auftraggeber section with three contact types in columns
    // Check for both structured fields and full_contact textarea fields
    const hasAuftraggeber = task.auftraggeber_full_contact || task.auftraggeber_name || task.auftraggeber_phone || task.auftraggeber_street || task.auftraggeber_city;
    const hasAppointment = task.appointment_full_contact || task.appointment_name || task.appointment_phone || task.appointment_street || task.appointment_city;
    const hasInvoice = task.invoice_full_contact || task.invoice_name || task.invoice_phone || task.invoice_street || task.invoice_city;

    // Determine if we should use full_contact (textarea) mode for each contact
    const useAuftraggeberFullContact = task.auftraggeber_use_full_contact === true && task.auftraggeber_full_contact;
    const useAppointmentFullContact = task.appointment_use_full_contact === true && task.appointment_full_contact;
    const useInvoiceFullContact = task.invoice_use_full_contact === true && task.invoice_full_contact;

    if (task.rapport_allow && (hasAuftraggeber || hasAppointment || hasInvoice)) {
        y += 10;
        y = addSection(doc, 'Kontaktdaten', y);

        // Column positions (thirds)
        const col1X = 20;   // Auftraggeber
        const col2X = 80;   // Terminvereinbarung
        const col3X = 140;  // Rechnungsempfänger
        const lineHeight = 5;

        // Add column headers
        addSmallUnderlinedLabel(doc, 'Auftraggeber', col1X, y);
        addSmallUnderlinedLabel(doc, 'Terminvereinbarung', col2X, y);
        addSmallUnderlinedLabel(doc, 'Rechnungsempfänger', col3X, y);
        y += 7;

        doc.setFont('Dax', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);

        const colWidth = 55;

        // Helper function to get contact lines for a column
        const getContactLines = (
            useFullContact: boolean | string | undefined,
            fullContact: string | undefined,
            name: string | undefined,
            phone: string | undefined,
            street: string | undefined,
            additionalStreet: string | undefined,
            postalCode: string | undefined,
            city: string | undefined
        ): string[] => {
            if (useFullContact && fullContact) {
                // Use full_contact textarea value - split by newlines and wrap each line
                const contactText = fullContact.trim();
                return doc.splitTextToSize(contactText, colWidth);
            } else {
                // Use structured fields
                const address = [
                    street,
                    additionalStreet,
                    [postalCode, city].filter(Boolean).join(' ')
                ].filter(Boolean).join(', ');

                const lines: string[] = [];
                lines.push(...doc.splitTextToSize(name || '-', colWidth));
                lines.push(...doc.splitTextToSize(phone || '-', colWidth));
                if (address) {
                    lines.push(...doc.splitTextToSize(address, colWidth));
                } else {
                    lines.push('-');
                }
                return lines;
            }
        };

        // Get lines for each contact column
        const auftraggberLines = getContactLines(
            useAuftraggeberFullContact,
            task.auftraggeber_full_contact,
            task.auftraggeber_name,
            task.auftraggeber_phone,
            task.auftraggeber_street,
            task.auftraggeber_additional_street,
            task.auftraggeber_postal_code,
            task.auftraggeber_city
        );
        const appointmentLines = getContactLines(
            useAppointmentFullContact,
            task.appointment_full_contact,
            task.appointment_name,
            task.appointment_phone,
            task.appointment_street,
            task.appointment_additional_street,
            task.appointment_postal_code,
            task.appointment_city
        );
        const invoiceLines = getContactLines(
            useInvoiceFullContact,
            task.invoice_full_contact,
            task.invoice_name,
            task.invoice_phone,
            task.invoice_street,
            task.invoice_additional_street,
            task.invoice_postal_code,
            task.invoice_city
        );

        // Render all lines for each column
        const maxLines = Math.max(auftraggberLines.length, appointmentLines.length, invoiceLines.length);

        for (let i = 0; i < maxLines; i++) {
            if (auftraggberLines[i]) doc.text(auftraggberLines[i], col1X, y);
            if (appointmentLines[i]) doc.text(appointmentLines[i], col2X, y);
            if (invoiceLines[i]) doc.text(invoiceLines[i], col3X, y);
            y += lineHeight;
        }

        y += 5;
    }

    return y;
};

/**
 * Add assignment section to PDF (Zuweisung)
 */
const addAssignmentSection = (
    doc: jsPDF,
    task: Task,
    currentY: number
): number => {
    let y = currentY + 12;

    // Ensure space for section header only
    y = ensureSpace(doc, y, 15);
    y = addSection(doc, 'Zuweisung', y);

    // Check space per row
    if (task.assigned_to_name) {
        y = ensureSpace(doc, y, 10);
        y = addKeyValue(doc, 'Zuweisen an', task.assigned_to_name, 20, y);
    } else if (task.assigned_to) {
        y = ensureSpace(doc, y, 10);
        y = addKeyValue(doc, 'Zuweisen an', task.assigned_to, 20, y);
    }

    if (task.created_by_name) {
        y = ensureSpace(doc, y, 10);
        y = addKeyValue(doc, 'Erstellt von', task.created_by_name, 20, y);
    } else if (task.created_by) {
        y = ensureSpace(doc, y, 10);
        y = addKeyValue(doc, 'Erstellt von', task.created_by, 20, y);
    }

    if (task.due_date) {
        y = ensureSpace(doc, y, 10);
        y = addKeyValue(doc, 'Fälligkeitsdatum', formatDate(task.due_date), 20, y);
    }

    return y;
};

/**
 * Add small underlined label (bold)
 */
const addSmallUnderlinedLabel = (
    doc: jsPDF,
    label: string,
    x: number,
    y: number
): void => {
    doc.setFontSize(10);
    doc.setFont('Dax', 'normal', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(label, x, y);

    const textWidth = doc.getTextWidth(label);
    doc.setLineWidth(0.2);
    doc.setDrawColor(0, 0, 0);
    doc.line(x, y + 1, x + textWidth, y + 1);
    // Reset to Dax font
    doc.setFont('Dax', 'normal');
};

/**
 * Add rapport description section to PDF
 */
const addDescriptionSection = (
    doc: jsPDF,
    rapportText: string | undefined,
    currentY: number
): number => {
    const pageHeight = doc.internal.pageSize.height;
    const marginBottom = 30;

    // Ensure space for section header
    let y = ensureSpace(doc, currentY + 10, 20);

    // Add Beschreibung section header
    y = addSection(doc, 'Durchgeführte Arbeiten', y);

    // Add rapport text
    if (rapportText && rapportText.trim()) {
        const rapportLines = doc.splitTextToSize(rapportText, 170);
        const lineHeight = 5;

        doc.setFont('Dax', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);

        // Print lines with page break handling
        for (let i = 0; i < rapportLines.length; i++) {
            // Check if this line fits on current page
            if (y + lineHeight > pageHeight - marginBottom) {
                doc.addPage();
                y = 30;
            }
            doc.text(rapportLines[i], 20, y);
            y += lineHeight;
        }

        y += 10;
    } else {
        doc.setFont('Dax', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(120, 120, 120);
        doc.text('Kein Rapport-Text vorhanden', 20, y);
        y += 15;
    }

    return y;
};

/**
 * Add three-column bottom section: Arbeitszeit | KFZ An-/Abfahrt | Anzahl Monteure
 */
const addBottomThreeColumnSection = (
    doc: jsPDF,
    currentY: number,
    arbeitszeit?: number,
    anfahrt_abfahrt_mit_kfz?: boolean,
    anzahl_monteure?: number,
    material?: string
): number => {
    const pageHeight = doc.internal.pageSize.height;
    const marginBottom = 30;

    let y = currentY + 15;

    // Column positions
    const col1X = 20;   // Arbeitszeit
    const col2X = 80;   // KFZ An-/Abfahrt
    const col3X = 140;  // Anzahl Monteure

    // Check space for header row (labels + values = ~15px)
    y = ensureSpace(doc, y, 15);

    // Add underlined headers
    addSmallUnderlinedLabel(doc, 'Arbeitszeit', col1X, y);
    addSmallUnderlinedLabel(doc, 'KFZ An-/Abfahrt', col2X, y);
    addSmallUnderlinedLabel(doc, 'Anzahl Monteure', col3X, y);

    y += 7;

    // Add values
    doc.setFont('Dax', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    // Arbeitszeit value
    const arbeitszeitText = arbeitszeit !== undefined && arbeitszeit > 0
        ? `Stunden: ${arbeitszeit}`
        : 'Stunden:';
    doc.text(arbeitszeitText, col1X, y);

    // KFZ An-/Abfahrt value
    const kfzText = anfahrt_abfahrt_mit_kfz !== undefined
        ? (anfahrt_abfahrt_mit_kfz ? 'Ja' : 'Nein')
        : 'Ja/Nein';
    doc.text(kfzText, col2X, y);

    // Anzahl Monteure value
    const monteureText = anzahl_monteure !== undefined && anzahl_monteure > 0
        ? anzahl_monteure.toString()
        : '';
    doc.text(monteureText, col3X, y);

    y += 15;

    // Add Material section if present
    if (material && material.trim()) {
        const materialLines = doc.splitTextToSize(material, 170);
        const lineHeight = 5;

        // Check space for material label
        y = ensureSpace(doc, y, 12);

        addSmallUnderlinedLabel(doc, 'Material', col1X, y);
        y += 7;

        doc.setFont('Dax', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);

        // Print material lines row by row with page break handling
        for (let i = 0; i < materialLines.length; i++) {
            if (y + lineHeight > pageHeight - marginBottom) {
                doc.addPage();
                y = 30;
            }
            doc.text(materialLines[i], col1X, y);
            y += lineHeight;
        }

        y += 5;
    }

    return y;
};

/**
 * Add single image to PDF with metadata
 */
const addSingleImage = async (
    doc: jsPDF,
    imageData: TaskImageBase64 | ImageData,
    imageNumber: number,
    currentY: number,
    isUploadedImage: boolean = false
): Promise<number> => {
    let y = currentY;

    // Image metadata
    doc.setFont('Dax', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);

    if ('uploaded_at' in imageData) {
        // TaskImageBase64
        doc.text(
            `Bild ${imageNumber}: Hochgeladen am ${formatDateTime(imageData.uploaded_at)} von ${imageData.uploaded_by}`,
            25,
            y
        );
    } else {
        // Uploaded ImageData
        doc.text(`Bild ${imageNumber}: Neu hochgeladen - ${imageData.name}`, 25, y);
    }
    y += 8;

    try {
        const imageStartY = y;
        const newY = await addImageToPDF(doc, imageData, 25, y, 150, 90);

        if (newY > y) {
            y = newY;
            doc.setDrawColor(180, 180, 180);
            doc.setLineWidth(0.3);
            doc.rect(23, imageStartY - 2, 154, y - imageStartY - 8);
        } else {
            throw new Error('Image could not be loaded');
        }

    } catch (error) {
        console.warn(`Could not add image to PDF:`, error);
        doc.setFont('Dax', 'normal');
        doc.setTextColor(120, 120, 120);
        doc.text(
            isUploadedImage ? 'Hochgeladenes Bild konnte nicht geladen werden' : 'Bild konnte nicht geladen werden',
            25,
            y
        );
        y += 20;
    }

    return y;
};

/**
 * Add all images to PDF with page breaks
 */
const addImagesSection = async (
    doc: jsPDF,
    taskImagesBase64: TaskImageBase64[],
    uploadedImagesBase64: ImageData[],
    totalPages: number,
    startY: number
): Promise<void> => {
    const pageHeight = doc.internal.pageSize.height;
    const spaceNeededForFirstImage = 130; // Header + first image approximate height

    let currentPageNumber: number;
    let currentY: number;

    // Check if there's enough space on the current page for images section
    if (startY + spaceNeededForFirstImage > pageHeight - 30) {
        // Not enough space, add a new page
        doc.addPage();
        currentPageNumber = 2;
        currentY = 20;
    } else {
        // Enough space, continue on the same page
        currentPageNumber = 1;
        currentY = startY + 10;
    }

    currentY = addSection(doc, 'Bilder', currentY);
    currentY += 5;

    // Add existing task images
    for (let i = 0; i < taskImagesBase64.length; i++) {
        const pageBreak = checkAndAddPageBreak(doc, currentY, currentPageNumber, totalPages);
        currentY = pageBreak.y;
        currentPageNumber = pageBreak.pageNumber;

        currentY = await addSingleImage(doc, taskImagesBase64[i], i + 1, currentY, false);

        if (i < taskImagesBase64.length - 1 || uploadedImagesBase64.length > 0) {
            currentY += 10;
        }
    }

    // Add uploaded images
    for (let j = 0; j < uploadedImagesBase64.length; j++) {
        const pageBreak = checkAndAddPageBreak(doc, currentY, currentPageNumber, totalPages);
        currentY = pageBreak.y;
        currentPageNumber = pageBreak.pageNumber;

        const imageNumber = taskImagesBase64.length + j + 1;
        currentY = await addSingleImage(doc, uploadedImagesBase64[j], imageNumber, currentY, true);

        if (j < uploadedImagesBase64.length - 1) {
            currentY += 10;
        }
    }

    addFooter(doc, currentPageNumber, totalPages);
};

/**
 * Load task images with fallback
 */
const loadImages = async (
    taskId: number,
    selectedImages: number[]
): Promise<{ useBase64: boolean; taskImagesBase64: TaskImageBase64[] }> => {
    if (!selectedImages || selectedImages.length === 0) {
        return {useBase64: false, taskImagesBase64: []};
    }

    try {
        const taskImagesBase64 = await loadTaskImagesBase64(taskId, selectedImages);
        return {useBase64: true, taskImagesBase64};
    } catch (error) {
        console.warn('Failed to load images as base64:', error);
        return {useBase64: false, taskImagesBase64: []};
    }
};

/**
 * Calculate total pages needed for PDF
 */
const calculateTotalPages = (imageCount: number, uploadedImageCount: number): number => {
    let totalPages = 1; // First page for task info
    if (imageCount > 0 || uploadedImageCount > 0) {
        totalPages += Math.ceil((imageCount + uploadedImageCount) / 2.5);
    }
    return totalPages;
};

/**
 * Create a rapport PDF from task data
 */
export const createRapport = async (params: CreateRapportParams): Promise<{ pdfBlob: Blob; fileName: string }> => {
    try {
        const {task, getRapportText, getStatusLabel} = params;

        // Load dependencies
        const bauvorhabenName = await loadBauvorhabenName(task.bauvorhaben_id);
        const closedTimestamp = new Date().toISOString();

        // Handle image selection
        let selectedImages = params.selectedImages;
        if (!selectedImages) {
            selectedImages = await showImageSelectionDialog(task.id);
        }

        // Load images
        const {useBase64, taskImagesBase64} = await loadImages(task.id, selectedImages || []);

        // Convert uploaded files
        let uploadedImagesBase64: ImageData[] = [];
        if (params.uploadedFiles && params.uploadedFiles.length > 0) {
            try {
                uploadedImagesBase64 = await convertFilesToBase64(params.uploadedFiles);
                console.log('Converted uploaded files to base64:', uploadedImagesBase64.length);
            } catch (error) {
                console.warn('Failed to convert uploaded files:', error);
            }
        }

        // Create PDF document
        const doc = new jsPDF();
        await loadCustomFont(doc);

        let currentY = 30;

        // Header with title and logo
        doc.setFontSize(32);
        doc.setFont('Dax', 'normal', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Rapport', 20, 28);
        // Reset to Dax font
        doc.setFont('Dax', 'normal');

        try {
            const logoResult = await tryLoadLogoImage();
            if (logoResult) {
                // Larger logo positioned in top right
                doc.addImage(logoResult.dataUrl, logoResult.format, 155, 5, 45, 45);
            }
        } catch (error) {
            console.warn('Could not load logo:', error);
        }

        currentY = 65;

        // Add task information (includes Auftraggeber)
        const statusText = getStatusLabel ? getStatusLabel(task.status) : (task.status || '');
        currentY = addTaskInfoSection(doc, task, currentY, statusText, closedTimestamp, params.bau || [], params.projekt_name);

        // Add description section
        const rapportText = getRapportText ? getRapportText() : task.rapport;
        currentY = addDescriptionSection(doc, rapportText, currentY);

        // Add assignment section (Zuweisung)
        currentY = addAssignmentSection(doc, task, currentY);

        // Add three-column bottom section (Arbeitszeit | KFZ An-/Abfahrt | Anzahl Monteure | Material)
        currentY = addBottomThreeColumnSection(
            doc,
            currentY,
            params.arbeitszeit,
            params.anfahrt_abfahrt_mit_kfz,
            params.anzahl_monteure,
            params.material
        );

        // Add signature if provided
        if (params.signature) {
            currentY += 10;
            currentY = await addSignatureSection(doc, params.signature, currentY);
        }

        // Calculate total pages
        const totalPages = calculateTotalPages(taskImagesBase64.length, uploadedImagesBase64.length);

        // Add footer to first page
        addFooter(doc, 1, totalPages);

        // Add images if available
        const hasImages = taskImagesBase64.length > 0 || uploadedImagesBase64.length > 0;
        if (hasImages) {
            await addImagesSection(doc, taskImagesBase64, uploadedImagesBase64, totalPages, currentY);
        }

        // Generate output
        const pdfBlob = doc.output('blob');
        const fileName = generateFileName(task.title);

        console.log('PDF generated as blob:', fileName);

        return {pdfBlob, fileName};

    } catch (error) {
        console.error('Error generating rapport PDF:', error);
        throw error;
    }
};

/**
 * Generate rapport PDF for a task (convenience function)
 */
export const generateRapportPDF = async (taskId: number): Promise<void> => {
    try {
        const task: Task = await TasksService.getTaskTaskTaskIdGet(taskId);
        const selectedImages = await showImageSelectionDialog(taskId);

        await createRapport({
            taskId,
            task,
            selectedImages,
        });

    } catch (error) {
        console.error('Error generating rapport PDF:', error);
        throw error;
    }
};

// Re-export dialog function for backwards compatibility
export {showImageSelectionDialog} from './rapportHelpers';
